import type { Dispatch } from 'react';

import type { ConfirmFn } from '../seams';
import type { LoggerMode } from './loggerMode';
import type { DraftEntry, LoggerAction } from './state/loggerDraftTypes';

// The two confirm dialogs of the timeline logger's focus card, extracted from
// useTimelineLogger (AP-0b 500-line split) — behaviour unchanged.
//
// Sciogli — dissolve the active group. Shared by the FocusPanel ⋯ menu and
// the SupersetTabs ⊗ control so there's one confirm dialog (the v2.67 smoke
// "no way out of superset" → a VISIBLE exit on the notebook header).
//
// Header ✕ — remove the active exercise. Confirmed (an entry with logged sets
// loses them), mirroring the card logger's EntryHeader so the two loggers
// behave identically; reuses workout.removeExercise* i18n. After removal the
// caller's onRemoved blanks the focus (Option B point c: no fallback —
// re-expand the roster so the athlete re-picks).
//
// ⭐ PE-44 — the same tap costs two different things, and the confirm has to
// say which. In the logger it takes the exercise out of the session in front
// of you. In the EDITOR the very same `remove_exercise` reaches removeSlot and
// deletes the slot from EVERY week it runs — a coach looking at week 2 taps it
// and loses weeks 1 to N, which the logger's wording ("from this workout",
// "any logged sets will be lost") does not describe and a prescription has no
// logged sets to lose. The control was never missing; the sentence was.
// ⛔ The week count is supplied by the MOUNT (removeExerciseAffectedWeeks),
// not derived here: this file sees the flat draft, where the other weeks do
// not exist. Absent → today's wording, unchanged.
//
// ⛔ Both go through the confirm SEAM, not react-native's Alert (RW-25):
// inside packages/ui an Alert is a no-op on react-native-web, so on the web
// the ⊗ that dissolves a superset and the ✕ that removes the focused exercise
// were both dead controls — they asked a question nobody ever saw.
export function useEntryDialogs(args: {
  t: (key: string, opts?: Record<string, unknown>) => string;
  dispatch: Dispatch<LoggerAction>;
  confirm: ConfirmFn;
  getExerciseNameBySlug: (slug: string, fallback: string) => string;
  activeEntry: DraftEntry | null;
  activeEntryIdx: number;
  mode: LoggerMode;
  /** PE-44 — weeks the removal would destroy; see loggerContext.ts. */
  removeExerciseAffectedWeeks?: number;
  /** Resets the focus state after a remove (activeId null + roster open). */
  onRemoved: () => void;
}) {
  const {
    t,
    dispatch,
    confirm,
    getExerciseNameBySlug,
    activeEntry,
    activeEntryIdx,
    mode,
    removeExerciseAffectedWeeks,
    onRemoved,
  } = args;

  const confirmSplitGroup = (groupId: string) => {
    void confirm({
      title: t('workout.splitGroupTitle', { defaultValue: 'Split this group?' }),
      body: t('workout.splitGroupBody', {
        defaultValue:
          'Exercises keep their sets and stay in the workout, unlinked. You can regroup them later.',
      }),
      confirmLabel: t('workout.splitGroupConfirm', { defaultValue: 'Split' }),
      cancelLabel: t('workout.cancel'),
    }).then((ok) => {
      if (ok) dispatch({ type: 'split_group', groupId });
    });
  };

  const onRemoveExercise = () => {
    if (!activeEntry || activeEntryIdx < 0) return;
    const name = getExerciseNameBySlug(
      activeEntry.exerciseId,
      activeEntry.exerciseName,
    );
    const weeks = removeExerciseAffectedWeeks;
    const prescribing = mode === 'prescribe' && weeks !== undefined && weeks > 0;
    void confirm({
      // ⭐ The TITLE carries the scope too, not only the body (a11y audit,
      // 30/08): it is the line that gets scanned first and read largest, and a
      // dialog whose headline is the logger's "Remove exercise" has already
      // told half a truth by the time the body arrives
      // ([[feedback_write_the_constraint_where_it_is_visible]]).
      title: prescribing
        ? t('composer.removeSlotConfirmTitle', { count: weeks })
        : t('workout.removeExercise'),
      // ⛔ No `defaultValue` twin on either key, unlike the split dialog above:
      // the i18nDefaultValueDrift gate reads `defaultValue:` and cannot see
      // `defaultValue_one`/`_other`, so a plural default would be the one copy
      // of the sentence nothing keeps in step with en.json.
      body: prescribing
        ? t('composer.removeSlotConfirm', { name, count: weeks })
        : t('workout.removeExerciseConfirm', { name }),
      // ⛔ The BUTTON stays "Remove exercise" in both modes, deliberately: it
      // names the ACTION, the title now names its reach, and it is the same
      // words as the 🗑 that opened the dialog (SupersetTabs' own label).
      confirmLabel: t('workout.removeExercise'),
      cancelLabel: t('workout.cancel'),
    }).then((ok) => {
      if (!ok) return;
      dispatch({ type: 'remove_exercise', entryIdx: activeEntryIdx });
      onRemoved();
    });
  };

  return { confirmSplitGroup, onRemoveExercise };
}
